import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { toSharePointHref } from '../../utils/sharepointUrl';
import { loadNomenclature, projectTypeList } from '../../utils/nomenclature';
import ProjectIdentityActions from './ProjectIdentityActions';

const ProjectDetailsModal = ({ isOpen, onClose, project, onProjectUpdated, isSuperadmin }) => {
  const { instance, accounts } = useMsal();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('details');
  
  // Data state
  const [projectData, setProjectData] = useState(null);
  const [divisions, setDivisions] = useState([]);
  // How the owning company was identified (manual / estimate letters / job
  // prefix / SharePoint) and whether those signals disagree.
  const [divisionResolution, setDivisionResolution] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [authorityLevels, setAuthorityLevels] = useState([]);
  
  // Reminder settings state
  const [reminderSettings, setReminderSettings] = useState(null);
  const [reminderPreview, setReminderPreview] = useState(null);
  const [loadingReminders, setLoadingReminders] = useState(false);
  const [savingReminders, setSavingReminders] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    job_number: '',
    job_name: '',
    client_name: '',
    project_type: '',
    division_id: '',
    estimate_number: '',
    discipline_ids: []
  });
  const [projectTypes, setProjectTypes] = useState(['Lump Sum', 'T&M', 'Unit Price', 'Cost Plus']);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');

  // Fetch all data when modal opens
  const fetchData = useCallback(async () => {
    if (!isOpen || !project || !accounts || accounts.length === 0) return;
    
    setLoading(true);
    setError('');
    try {
      const [projectRes, divisionsRes, disciplinesRes, usersRes, assignedRes, roleTemplatesRes, authorityLevelsRes, nom, divResRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/projects/${project.id}`),
        authenticatedApiCall(instance, accounts, 'GET', '/projects/meta/divisions'),
        authenticatedApiCall(instance, accounts, 'GET', '/projects/meta/disciplines'),
        authenticatedApiCall(instance, accounts, 'GET', '/users'),
        authenticatedApiCall(instance, accounts, 'GET', `/project-assignments/project/${project.id}/users`),
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates'),
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates/authority-levels'),
        loadNomenclature((method, url, data) => authenticatedApiCall(instance, accounts, method, url, data)).catch(() => null),
        authenticatedApiCall(instance, accounts, 'GET', `/projects/${project.id}/division-resolution`).catch(() => null),
      ]);
      
      const projData = projectRes.data.project;
      setProjectData(projData);
      setDivisionResolution(divResRes?.data?.resolution || null);
      setDivisions(divisionsRes.data.data || []);
      setDisciplines(disciplinesRes.data.data || []);
      setAllUsers(usersRes.data.users || []);
      setAssignedUsers(assignedRes.data.data || []);
      setRoleTemplates(roleTemplatesRes.data.data || []);
      setAuthorityLevels(authorityLevelsRes.data.data || []);
      if (nom) setProjectTypes(projectTypeList(nom));
      
      // Initialize form data
      setFormData({
        job_number: projData.job_number || '',
        job_name: projData.job_name || '',
        client_name: projData.client_name || '',
        project_type: projData.project_type || '',
        division_id: projData.division_id || '',
        estimate_number: projData.estimate_number || '',
        discipline_ids: projData.disciplines?.map(d => d.id) || []
      });
    } catch (err) {
      console.error('Error fetching project data:', err);
      setError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  }, [isOpen, project, instance, accounts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch reminder settings when notifications tab becomes active
  const fetchReminderData = useCallback(async () => {
    if (!project || !accounts || accounts.length === 0) return;
    
    setLoadingReminders(true);
    try {
      const [settingsRes, previewRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/projects/${project.id}/reminder-settings`),
        authenticatedApiCall(instance, accounts, 'GET', `/projects/${project.id}/reminder-preview`)
      ]);
      
      setReminderSettings(settingsRes.data.data);
      setReminderPreview(previewRes.data.data);
    } catch (err) {
      console.error('Error fetching reminder data:', err);
      setError('Failed to load reminder settings');
    } finally {
      setLoadingReminders(false);
    }
  }, [project, instance, accounts]);

  useEffect(() => {
    if (activeTab === 'notifications' && !reminderSettings) {
      fetchReminderData();
    }
  }, [activeTab, reminderSettings, fetchReminderData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDisciplineToggle = (disciplineId) => {
    setFormData(prev => {
      const isSelected = prev.discipline_ids.includes(disciplineId);
      return {
        ...prev,
        discipline_ids: isSelected
          ? prev.discipline_ids.filter(id => id !== disciplineId)
          : [...prev.discipline_ids, disciplineId]
      };
    });
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/projects/${project.id}`,
        {
          job_number: formData.job_number,
          job_name: formData.job_name,
          client_name: formData.client_name || null,
          project_type: formData.project_type || null,
          division_id: formData.division_id || null,
          estimate_number: projectData?.estimate_number || formData.estimate_number || null,
          discipline_ids: formData.discipline_ids
        }
      );

      setSuccessMessage('Project updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh project data
      fetchData();
      
      // Notify parent
      if (onProjectUpdated) {
        onProjectUpdated(project.id);
      }
    } catch (err) {
      console.error('Error updating project:', err);
      setError(err.response?.data?.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignUser = async (userId) => {
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        '/project-assignments/assign',
        { userId, projectId: project.id, roleOverride: null }
      );
      fetchData();
    } catch (err) {
      console.error('Error assigning user:', err);
      setError('Failed to assign user');
    }
  };

  const handleUnassignUser = async (userId) => {
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/project-assignments/unassign?userId=${userId}&projectId=${project.id}`
      );
      fetchData();
    } catch (err) {
      console.error('Error unassigning user:', err);
      setError('Failed to unassign user');
    }
  };

  // V2: Update role override using role_template_id and optional authority_level_override_id
  const handleRoleOverrideChange = async (userId, roleOverrideId, authorityLevelOverrideId = null) => {
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        '/project-assignments/role-override',
        { 
          userId, 
          projectId: project.id, 
          role_override_id: roleOverrideId || null,
          authority_level_override_id: authorityLevelOverrideId
        }
      );
      fetchData();
      setSuccessMessage('Role override updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating role override:', err);
      setError('Failed to update role override');
    }
  };

  // Handle authority level override change separately
  const handleAuthorityLevelChange = async (userId, authorityLevelOverrideId) => {
    // Find the user to get their current role override
    const user = assignedUsers.find(u => u.id === userId);
    if (!user) return;
    
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        '/project-assignments/role-override',
        { 
          userId, 
          projectId: project.id, 
          role_override_id: user.role_override_id || null,
          authority_level_override_id: authorityLevelOverrideId || null
        }
      );
      fetchData();
      setSuccessMessage('Authority level updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating authority level:', err);
      setError('Failed to update authority level');
    }
  };

  const filteredUnassignedUsers = allUsers.filter(user => {
    const isAssigned = assignedUsers.some(au => au.id === user.id);
    if (isAssigned) return false;
    
    const searchLower = userSearchTerm.toLowerCase();
    return (
      user.display_name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower)
    );
  });

  // Filter role templates to only show Construction roles for project overrides (V2)
  // Projects are construction-focused, so only construction roles should be available as overrides
  const constructionRoles = useMemo(() => {
    return (roleTemplates || []).filter(role => 
      role.department_name === 'Construction' && 
      role.id !== 'superadmin'
    );
  }, [roleTemplates]);

  // Reminder settings handlers
  const handleReminderEnabledToggle = async (enabled) => {
    setSavingReminders(true);
    setError('');
    setSuccessMessage('');
    
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/projects/${project.id}/reminder-settings`,
        { reminder_enabled: enabled }
      );
      
      setReminderSettings(prev => ({ ...prev, reminder_enabled: enabled }));
      setSuccessMessage(`Reminders ${enabled ? 'enabled' : 'disabled'} successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating reminder settings:', err);
      setError('Failed to update reminder settings');
    } finally {
      setSavingReminders(false);
    }
  };

  const handleDaysThresholdChange = async (days) => {
    setSavingReminders(true);
    setError('');
    setSuccessMessage('');
    
    try {
      await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/projects/${project.id}/reminder-settings`,
        { reminder_days_threshold: parseInt(days) }
      );
      
      setReminderSettings(prev => ({ ...prev, reminder_days_threshold: parseInt(days) }));
      setSuccessMessage('Inactivity threshold updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Refresh preview since threshold changed
      fetchReminderData();
    } catch (err) {
      console.error('Error updating reminder settings:', err);
      setError('Failed to update reminder settings');
    } finally {
      setSavingReminders(false);
    }
  };

  const handleSendTestEmail = async () => {
    setSendingTestEmail(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/projects/${project.id}/test-reminder`,
        { sendToAdmin: true }
      );
      
      setSuccessMessage(response.data.message || 'Test email sent successfully');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error sending test email:', err);
      setError('Failed to send test email: ' + (err.response?.data?.message || err.message));
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    },
    modal: {
      backgroundColor: '#fff',
      borderRadius: '12px',
      width: '90%',
      maxWidth: '950px',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
      display: 'flex',
      flexDirection: 'column'
    },
    header: {
      padding: '1.5rem 2rem',
      borderBottom: '1px solid #e9ecef',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f8f9fa'
    },
    title: {
      margin: 0,
      color: '#333',
      fontSize: '1.25rem',
      fontWeight: '600'
    },
    subtitle: {
      margin: '0.25rem 0 0 0',
      color: '#666',
      fontSize: '0.9rem'
    },
    closeButton: {
      background: 'none',
      border: 'none',
      fontSize: '1.5rem',
      cursor: 'pointer',
      color: '#666',
      padding: '0.25rem',
      lineHeight: 1
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid #e9ecef',
      backgroundColor: '#fff'
    },
    tab: (isActive) => ({
      padding: '1rem 1.5rem',
      border: 'none',
      backgroundColor: 'transparent',
      borderBottom: isActive ? '2px solid #007bff' : '2px solid transparent',
      color: isActive ? '#007bff' : '#666',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: isActive ? '500' : '400',
      transition: 'all 0.2s ease'
    }),
    content: {
      padding: '2rem',
      overflowY: 'auto',
      flex: 1
    },
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: '500',
      color: '#333',
      fontSize: '0.9rem'
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #ced4da',
      borderRadius: '6px',
      fontSize: '1rem',
      boxSizing: 'border-box',
      transition: 'border-color 0.2s ease'
    },
    select: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #ced4da',
      borderRadius: '6px',
      fontSize: '1rem',
      boxSizing: 'border-box',
      cursor: 'pointer'
    },
    roleSelect: {
      padding: '0.4rem 0.5rem',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      fontSize: '0.8rem',
      backgroundColor: '#fff',
      cursor: 'pointer',
      minWidth: '150px'
    },
    checkboxGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: '0.5rem'
    },
    checkboxItem: (isSelected) => ({
      display: 'flex',
      alignItems: 'center',
      padding: '0.5rem 0.75rem',
      backgroundColor: isSelected ? '#e7f3ff' : '#f8f9fa',
      borderRadius: '6px',
      cursor: 'pointer',
      border: `1px solid ${isSelected ? '#007bff' : '#e9ecef'}`,
      transition: 'all 0.2s ease'
    }),
    checkbox: {
      marginRight: '0.5rem',
      width: '16px',
      height: '16px',
      accentColor: '#007bff'
    },
    userList: {
      border: '1px solid #e9ecef',
      borderRadius: '6px',
      maxHeight: '300px',
      overflowY: 'auto'
    },
    userItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.75rem 1rem',
      borderBottom: '1px solid #e9ecef',
      gap: '1rem'
    },
    userInfo: {
      flex: 1,
      minWidth: 0
    },
    userName: {
      fontWeight: '500',
      color: '#333'
    },
    userEmail: {
      fontSize: '0.85rem',
      color: '#666'
    },
    userRoleInfo: {
      fontSize: '0.75rem',
      color: '#888',
      marginTop: '0.25rem'
    },
    roleOverrideSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      flexShrink: 0
    },
    button: (variant) => ({
      padding: '0.5rem 1rem',
      backgroundColor: variant === 'primary' ? '#007bff' : variant === 'success' ? '#28a745' : variant === 'danger' ? '#dc3545' : '#6c757d',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.9rem',
      fontWeight: '500',
      transition: 'all 0.2s ease'
    }),
    buttonSmall: (variant) => ({
      padding: '0.25rem 0.5rem',
      backgroundColor: variant === 'primary' ? '#007bff' : variant === 'success' ? '#28a745' : variant === 'danger' ? '#dc3545' : variant === 'warning' ? '#ffc107' : '#6c757d',
      color: variant === 'warning' ? '#212529' : 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8rem',
      marginLeft: '0.25rem'
    }),
    alert: (type) => ({
      padding: '0.75rem 1rem',
      backgroundColor: type === 'error' ? '#f8d7da' : '#d4edda',
      color: type === 'error' ? '#721c24' : '#155724',
      borderRadius: '6px',
      marginBottom: '1rem',
      border: `1px solid ${type === 'error' ? '#f5c6cb' : '#c3e6cb'}`
    }),
    roleBadge: (hasOverride) => ({
      display: 'inline-block',
      padding: '0.125rem 0.5rem',
      backgroundColor: hasOverride ? '#17a2b8' : '#e9ecef',
      color: hasOverride ? 'white' : '#666',
      borderRadius: '4px',
      fontSize: '0.7rem',
      marginLeft: '0.5rem'
    }),
    footer: {
      padding: '1rem 2rem',
      borderTop: '1px solid #e9ecef',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      backgroundColor: '#f8f9fa'
    },
    searchInput: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      border: '1px solid #ced4da',
      borderRadius: '6px',
      fontSize: '0.9rem',
      marginBottom: '0.75rem'
    },
    sectionTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#333',
      marginBottom: '0.75rem',
      marginTop: '1.5rem'
    },
    helpText: {
      color: '#666',
      fontSize: '0.8rem',
      marginTop: '0.75rem',
      padding: '0.75rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: '1px solid #e9ecef'
    }
  };

  const renderDetailsTab = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Job Number</label>
          <input
            type="text"
            name="job_number"
            value={formData.job_number}
            disabled
            style={{
              ...styles.input,
              backgroundColor: '#e9ecef',
              cursor: 'not-allowed',
              color: '#495057'
            }}
            title="Job number cannot be changed after creation due to database relationships"
          />
          <p style={{ 
            margin: '0.25rem 0 0 0', 
            fontSize: '0.75rem', 
            color: '#6c757d',
            fontStyle: 'italic'
          }}>
            Locked because this number is the key in trackers, time, and almost every other table. Superadmins can change it only while the job is still empty.
          </p>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Job Name</label>
          <input
            type="text"
            name="job_name"
            value={formData.job_name}
            onChange={handleInputChange}
            style={styles.input}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Division</label>
          <select
            name="division_id"
            value={formData.division_id}
            onChange={handleInputChange}
            style={styles.select}
          >
            <option value="">-- No Division --</option>
            {divisions.map(div => (
              <option key={div.id} value={div.id}>
                {div.division_number} - {div.division_name}
              </option>
            ))}
          </select>
          {divisionResolution && (
            <div style={{ marginTop: '6px', fontSize: '12px', lineHeight: 1.45 }}>
              {divisionResolution.divisionId ? (
                <span style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: '999px',
                  backgroundColor: divisionResolution.source === 'manual' ? '#eef2ff' : '#ecfdf5',
                  color: divisionResolution.source === 'manual' ? '#3730a3' : '#065f46',
                  border: `1px solid ${divisionResolution.source === 'manual' ? '#c7d2fe' : '#a7f3d0'}`,
                  fontWeight: 600,
                }}
                  title={divisionResolution.detail || ''}
                >
                  {divisionResolution.sourceLabel}
                  {divisionResolution.detail ? ` · ${divisionResolution.detail}` : ''}
                  {divisionResolution.currency ? ` · reports in ${divisionResolution.currency}` : ''}
                </span>
              ) : (
                <span style={{ color: '#92400e' }}>
                  No company can be derived for this job (estimate number and job-number prefix do not identify one). Pick the Division or link the estimate.
                </span>
              )}
              {!formData.division_id && divisionResolution.divisionId && (
                <div style={{ color: '#6b7280', marginTop: '4px' }}>
                  Saving with "No Division" will auto-fill {divisionResolution.divisionNumber} {divisionResolution.divisionName}.
                </div>
              )}
              {divisionResolution.conflicts?.length > 0 && (
                <div style={{
                  marginTop: '6px', padding: '6px 10px', backgroundColor: '#fffbeb',
                  border: '1px solid #fde68a', borderRadius: '6px', color: '#92400e',
                }}>
                  <strong>Company check:</strong> {divisionResolution.sourceLabel} says{' '}
                  {divisionResolution.divisionNumber} {divisionResolution.divisionName}, but{' '}
                  {divisionResolution.conflicts.map((c, i) => (
                    <span key={`${c.source}-${c.divisionNumber}`}>
                      {i > 0 ? '; ' : ''}the {c.sourceLabel} says {c.divisionNumber} {c.divisionName}
                      {c.detail ? ` (${c.detail})` : ''}
                    </span>
                  ))}
                  . Confirm with Accounting.
                </div>
              )}
            </div>
          )}
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Estimate #</label>
          <input
            type="text"
            name="estimate_number"
            value={formData.estimate_number}
            disabled
            placeholder="Linked from Award"
            style={{
              ...styles.input,
              backgroundColor: '#e9ecef',
              cursor: 'not-allowed',
              color: '#495057'
            }}
            title="Use Change linked estimate to move this job onto a different bid"
          />
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic' }}>
            Changing this by hand would leave the old estimate still marked awarded. Superadmins use Change linked estimate below.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={styles.formGroup}>
          <label style={styles.label}>Client</label>
          <input
            type="text"
            name="client_name"
            value={formData.client_name}
            onChange={handleInputChange}
            placeholder="Enter client name (optional)"
            style={styles.input}
          />
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>Project type</label>
          <select
            name="project_type"
            value={formData.project_type}
            onChange={handleInputChange}
            style={styles.select || styles.input}
          >
            <option value="">-- Not set (PDFs show …) --</option>
            {projectTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div style={styles.formGroup}>
          <label style={styles.label}>SharePoint</label>
          {projectData?.sharepoint_url ? (
            <a
              href={toSharePointHref(projectData.sharepoint_url)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1rem',
                color: '#0d6efd',
                textDecoration: 'none',
                fontSize: '0.95rem',
                wordBreak: 'break-all'
              }}
            >
              Open job SharePoint site ↗
            </a>
          ) : (
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#6c757d', fontStyle: 'italic' }}>
              No SharePoint link yet — filled overnight by the awarded-job crawler.
            </p>
          )}
        </div>
      </div>

      <div style={styles.formGroup}>
        <label style={styles.label}>Disciplines</label>
        <div style={styles.checkboxGrid}>
          {disciplines.map(disc => (
            <div
              key={disc.id}
              style={styles.checkboxItem(formData.discipline_ids.includes(disc.id))}
              onClick={() => handleDisciplineToggle(disc.id)}
            >
              <input
                type="checkbox"
                checked={formData.discipline_ids.includes(disc.id)}
                onChange={() => {}}
                style={styles.checkbox}
              />
              <span style={{ color: '#333', fontSize: '0.9rem' }}>{disc.discipline_name}</span>
            </div>
          ))}
        </div>
      </div>

      <ProjectIdentityActions
        project={projectData || project}
        isSuperadmin={isSuperadmin}
        onChanged={() => {
          fetchData();
          if (onProjectUpdated) onProjectUpdated(project.id);
        }}
      />
    </>
  );

  const renderNotificationsTab = () => {
    if (loadingReminders) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Loading reminder settings...
        </div>
      );
    }

    if (!reminderSettings) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          Failed to load reminder settings
        </div>
      );
    }

    return (
      <>
        <h4 style={{ ...styles.sectionTitle, marginTop: 0 }}>
          Progress Tracker Update Reminders
        </h4>
        
        <div style={styles.helpText}>
          Automatic email reminders are sent to the Technical Coordinator of each discipline who
          hasn't updated the progress tracker within the specified timeframe. Reminders are sent
          every Friday at 12:00 AM (CST).
        </div>

        {/* Enable/Disable Toggle */}
        <div style={{ 
          ...styles.formGroup, 
          marginTop: '1.5rem',
          padding: '1rem',
          backgroundColor: reminderSettings.reminder_enabled ? '#d4edda' : '#f8f9fa',
          borderRadius: '8px',
          border: `1px solid ${reminderSettings.reminder_enabled ? '#c3e6cb' : '#e9ecef'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <label style={{ ...styles.label, marginBottom: 0 }}>
                Reminder Emails
              </label>
              <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
                {reminderSettings.reminder_enabled 
                  ? 'Users will receive reminder emails when inactive' 
                  : 'Reminder emails are disabled for this project'}
              </p>
            </div>
            <button
              onClick={() => handleReminderEnabledToggle(!reminderSettings.reminder_enabled)}
              disabled={savingReminders}
              style={{
                ...styles.button(reminderSettings.reminder_enabled ? 'danger' : 'success'),
                opacity: savingReminders ? 0.6 : 1
              }}
            >
              {savingReminders ? 'Saving...' : (reminderSettings.reminder_enabled ? 'Disable' : 'Enable')}
            </button>
          </div>
        </div>

        {/* Inactivity Threshold */}
        <div style={{ ...styles.formGroup, marginTop: '1.5rem' }}>
          <label style={styles.label}>Inactivity Threshold (Days)</label>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#666' }}>
            Send reminder if a user hasn't updated for this many days
          </p>
          <select
            value={reminderSettings.reminder_days_threshold || 5}
            onChange={(e) => handleDaysThresholdChange(e.target.value)}
            disabled={savingReminders || !reminderSettings.reminder_enabled}
            style={{
              ...styles.select,
              maxWidth: '200px',
              opacity: reminderSettings.reminder_enabled ? 1 : 0.6
            }}
          >
            {[1, 2, 3, 4, 5, 7, 10, 14, 21, 30].map(days => (
              <option key={days} value={days}>
                {days} {days === 1 ? 'day' : 'days'}
              </option>
            ))}
          </select>
        </div>

        {/* Statistics */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '1rem',
          marginTop: '1.5rem'
        }}>
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#007bff' }}>
              {reminderSettings.assigned_users || 0}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Assigned Users</div>
          </div>
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#28a745' }}>
              {reminderSettings.active_users || 0}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Active Users</div>
          </div>
          <div style={{
            padding: '1rem',
            backgroundColor: reminderPreview?.usersCount > 0 ? '#fff3cd' : '#f8f9fa',
            borderRadius: '8px',
            textAlign: 'center',
            border: reminderPreview?.usersCount > 0 ? '1px solid #ffc107' : 'none'
          }}>
            <div style={{ 
              fontSize: '1.5rem', 
              fontWeight: '600', 
              color: reminderPreview?.usersCount > 0 ? '#856404' : '#666' 
            }}>
              {reminderPreview?.usersCount || 0}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#666' }}>Would Receive Reminder</div>
          </div>
        </div>

        {/* Preview of Users Who Would Receive Reminders */}
        {reminderPreview?.users && reminderPreview.users.length > 0 && (
          <>
            <h4 style={styles.sectionTitle}>
              Users Who Would Receive Reminders
            </h4>
            <div style={styles.userList}>
              {reminderPreview.users.map((user, idx) => (
                <div key={idx} style={styles.userItem}>
                  <div style={styles.userInfo}>
                    <div style={styles.userName}>{user.displayName}</div>
                    <div style={styles.userEmail}>{user.email}</div>
                  </div>
                  <span style={{ 
                    color: '#dc3545', 
                    fontSize: '0.85rem',
                    fontWeight: '500'
                  }}>
                    {user.lastUpdate}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Last Reminder Sent */}
        {reminderSettings.last_reminder_sent_at && (
          <div style={{ 
            marginTop: '1.5rem', 
            padding: '0.75rem',
            backgroundColor: '#e7f3ff',
            borderRadius: '6px',
            fontSize: '0.85rem',
            color: '#004085'
          }}>
            <strong>Last reminder sent:</strong>{' '}
            {new Date(reminderSettings.last_reminder_sent_at).toLocaleString('en-US', {
              timeZone: 'America/Chicago',
              dateStyle: 'medium',
              timeStyle: 'short'
            })} CST
          </div>
        )}

        {/* Test Email Button */}
        <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e9ecef' }}>
          <button
            onClick={handleSendTestEmail}
            disabled={sendingTestEmail || !reminderSettings.reminder_enabled}
            style={{
              ...styles.button('primary'),
              opacity: (sendingTestEmail || !reminderSettings.reminder_enabled) ? 0.6 : 1
            }}
          >
            {sendingTestEmail ? 'Sending...' : '📧 Send Test Email to Me'}
          </button>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#666' }}>
            Send a test reminder email to your email address to preview the email format
          </p>
        </div>

        {/* Schedule Info */}
        {reminderSettings.globalSchedule && (
          <div style={{ 
            ...styles.helpText,
            marginTop: '1.5rem'
          }}>
            <strong>📅 Global Schedule:</strong> {reminderSettings.globalSchedule.enabled 
              ? `Next reminder run: ${reminderSettings.globalSchedule.nextRunLocal || 'Calculating...'}`
              : 'Scheduler not running'}
          </div>
        )}
      </>
    );
  };

  const renderUsersTab = () => (
    <>
      <h4 style={{ ...styles.sectionTitle, marginTop: 0 }}>
        Assigned Users ({assignedUsers.length})
      </h4>
      {assignedUsers.length === 0 ? (
        <p style={{ color: '#666', fontStyle: 'italic' }}>No users assigned to this project</p>
      ) : (
        <div style={styles.userList}>
          {assignedUsers.map(user => (
            <div key={user.id} style={styles.userItem}>
              <div style={styles.userInfo}>
                <div style={styles.userName}>
                  {user.display_name}
                  {user.role_override_id && (
                    <span style={styles.roleBadge(true)}>
                      Override: {user.override_role_name}
                    </span>
                  )}
                </div>
                <div style={styles.userEmail}>{user.email}</div>
                <div style={styles.userRoleInfo}>
                  Default: {user.primary_role_name || 'Not set'}
                  {user.role_override_id && (
                    <> | Project Role: {user.override_role_name}</>
                  )}
                </div>
              </div>
              <div style={styles.roleOverrideSection}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                  {/* Role Override Dropdown */}
                  <select
                    value={user.role_override_id || ''}
                    onChange={(e) => handleRoleOverrideChange(user.id, e.target.value)}
                    style={styles.roleSelect}
                  >
                    <option value="">Use Default ({user.primary_role_name || 'None'})</option>
                    {constructionRoles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.display_name}
                      </option>
                    ))}
                  </select>
                  {/* Authority Level Override Dropdown */}
                  <select
                    value={user.authority_level_override_id || ''}
                    onChange={(e) => handleAuthorityLevelChange(user.id, e.target.value)}
                    style={{...styles.roleSelect, fontSize: '12px'}}
                  >
                    <option value="">
                      Authority: Use Role Default ({user.override_role_authority_level || user.primary_authority_level || 'Staff'})
                    </option>
                    {authorityLevels.filter(al => al.name !== 'System').map(al => (
                      <option key={al.id} value={al.id}>
                        Authority: {al.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => handleUnassignUser(user.id)}
                  style={{...styles.buttonSmall('danger'), alignSelf: 'flex-start', marginTop: '4px'}}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      
      <div style={styles.helpText}>
        <strong>Role Override:</strong> Select a Construction role to override the user's default permissions for this project. 
        <strong> Authority Level:</strong> Optionally override the authority level separately from the role.
        "Use Default" keeps the user's normal permissions.
      </div>

      <h4 style={styles.sectionTitle}>Add Users</h4>
      <input
        type="text"
        placeholder="Search users..."
        value={userSearchTerm}
        onChange={(e) => setUserSearchTerm(e.target.value)}
        style={styles.searchInput}
      />
      <div style={styles.userList}>
        {filteredUnassignedUsers.length === 0 ? (
          <p style={{ color: '#666', fontStyle: 'italic', padding: '1rem', textAlign: 'center' }}>
            {userSearchTerm ? 'No users found' : 'All users are assigned'}
          </p>
        ) : (
          filteredUnassignedUsers.slice(0, 20).map(user => (
            <div key={user.id} style={styles.userItem}>
              <div style={styles.userInfo}>
                <div style={styles.userName}>{user.display_name}</div>
                <div style={styles.userEmail}>{user.email}</div>
              </div>
              <button
                onClick={() => handleAssignUser(user.id)}
                style={styles.buttonSmall('success')}
              >
                + Assign
              </button>
            </div>
          ))
        )}
        {filteredUnassignedUsers.length > 20 && (
          <p style={{ color: '#666', fontSize: '0.85rem', padding: '0.5rem 1rem', textAlign: 'center' }}>
            Showing first 20 results. Use search to find more.
          </p>
        )}
      </div>
    </>
  );

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>Project Settings</h3>
            <p style={styles.subtitle}>{projectData?.job_number} - {projectData?.job_name}</p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>×</button>
        </div>

        <div style={styles.tabs}>
          <button
            style={styles.tab(activeTab === 'details')}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button
            style={styles.tab(activeTab === 'users')}
            onClick={() => setActiveTab('users')}
          >
            Users ({assignedUsers.length})
          </button>
          <button
            style={styles.tab(activeTab === 'notifications')}
            onClick={() => setActiveTab('notifications')}
          >
            Notifications
          </button>
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Loading project data...
            </div>
          ) : (
            <>
              {error && <div style={styles.alert('error')}>{error}</div>}
              {successMessage && <div style={styles.alert('success')}>{successMessage}</div>}
              
              {activeTab === 'details' && renderDetailsTab()}
              {activeTab === 'users' && renderUsersTab()}
              {activeTab === 'notifications' && renderNotificationsTab()}
            </>
          )}
        </div>

        {activeTab === 'details' && (
          <div style={styles.footer}>
            <button onClick={onClose} style={styles.button('secondary')}>
              Close
            </button>
            <button
              onClick={handleSaveDetails}
              disabled={saving}
              style={{
                ...styles.button('primary'),
                opacity: saving ? 0.6 : 1,
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}

        {activeTab === 'users' && (
          <div style={styles.footer}>
            <button onClick={onClose} style={styles.button('secondary')}>
              Close
            </button>
          </div>
        )}

        {activeTab === 'notifications' && (
          <div style={styles.footer}>
            <button onClick={onClose} style={styles.button('secondary')}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetailsModal;
